import {Page} from '@vio/core/src/analytics/types/Events'

/**
 * Checks if the current page is the notification preferences page
 * @param analyticsPageName - The current page name from analytics
 * @returns True if the page is notification preferences
 */
export const isNotificationPreferencesPage = (
  analyticsPageName: string
): boolean => {
  return analyticsPageName === Page.NotificationPreferences
}

/**
 * URL parameters for notification preferences page
 */
export interface NotificationPreferencesUrlParams {
  /** Email address from URL parameter (for unsubscribe links) */
  email: string | null
  /** Whether to unsubscribe from all notifications */
  unsubscribeAll: boolean
  /** Token from URL parameter (for unsubscribe links) */
  token: string | null
}

/**
 * Extracts URL search parameters relevant to notification preferences
 * Used for handling unsubscribe links and email-based access
 * @returns Object containing email and unsubscribe parameters
 */
export const getNotificationPreferencesUrlParams =
  (): NotificationPreferencesUrlParams => {
    if (typeof window === 'undefined') {
      return {
        email: null,
        unsubscribeAll: false,
        token: null
      }
    }

    const searchParams = new URLSearchParams(window.location.search)
    return {
      email: searchParams.get('email'),
      unsubscribeAll: searchParams.get('unsubscribe') === 'all',
      token: searchParams.get('token')
    }
  }

/**
 * Determines if the notification preferences should be displayed in a stateless mode
 * (without user authentication or with mismatched email)
 * @param isAuthenticated - Whether the user is currently authenticated
 * @param analyticsPageName - Current page name from analytics
 * @param userEmail - Email of the authenticated user
 * @returns True if the page should display in stateless mode
 */
export const shouldDisplayStatelessNotificationPreferences = (
  isAuthenticated: boolean,
  analyticsPageName: string,
  userEmail: string | null | undefined
): boolean => {
  const urlParams = getNotificationPreferencesUrlParams()

  if (!isNotificationPreferencesPage(analyticsPageName)) {
    return false
  }

  return Boolean(
    // User is not authenticated but on notification preferences page
    !isAuthenticated ||
      // URL email parameter doesn't match authenticated user email (only if we have both emails to compare)
      (urlParams.email !== null && userEmail && urlParams.email !== userEmail)
  )
}
